import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Table, type Column } from 'grabbitbot-legacy-ui'
import { adminClient } from '../../api'
import type { AdminProduct, AdminSubscription } from '../../api/adminTypes'
import { formatDateTime } from '../trading/tradingConstants'
import { AdminNotice } from './adminShared'
import { describeAdminError, formatMoney } from './adminHelpers'
/**
 * Equivalent of legacy `AdminPaymentDashboardComponent` (route
 * `admin/payment`, contract #3/#4 without `email` — every subscription and
 * every product). Per the audit: "subscriptions/products overview, users
 * missing a subscription or product" — rows with `hasWarning`/`hasError`
 * (subscriptions) or a failed payment (`failCount > 0`, products) are
 * flagged so an admin can triage without reading every row.
 */
export function AdminPaymentDashboardPage() {
  const [subscriptions, setSubscriptions] = useState<AdminSubscription[] | null>(null)
  const [products, setProducts] = useState<AdminProduct[] | null>(null)
  const [feedback, setFeedback] = useState<ReturnType<typeof describeAdminError> | undefined>()

  useEffect(() => {
    adminClient
      .subscriptions()
      .then(setSubscriptions)
      .catch((err) => setFeedback(describeAdminError(err)))
    adminClient
      .products()
      .then(setProducts)
      .catch((err) => setFeedback(describeAdminError(err)))
  }, [])

  const subColumns: Column<AdminSubscription>[] = [
    {
      key: 'email',
      label: 'E-mail',
      align: 'left',
      sortValue: (r) => r.email ?? '',
      format: (_v, r) => <Link to={`/admin/payment/user?email=${encodeURIComponent(r.email ?? '')}`}>{r.email}</Link>,
    },
    { key: 'plan', label: 'Plan', align: 'left', sortValue: (r) => r.plan ?? '' },
    { key: 'status', label: 'Status', align: 'right', numeric: true, sortValue: (r) => r.status ?? 0 },
    { key: 'monthlyFee', label: 'Monthly fee', align: 'right', numeric: true, sortValue: (r) => r.monthlyFee ?? 0, format: (_v, r) => formatMoney(r.monthlyFee, r.currency) },
    { key: 'expiresAt', label: 'Expires', align: 'left', sortValue: (r) => r.expiresAt ?? '', format: (v) => formatDateTime(v as string) },
    {
      key: 'flags',
      label: 'Flags',
      sortable: false,
      format: (_v, r) => (
        <>
          {r.hasWarning ? <span className="portal-notice portal-notice--warning">warning</span> : null}
          {r.hasError ? <span className="portal-notice portal-notice--error">error</span> : null}
        </>
      ),
    },
  ]

  const productColumns: Column<AdminProduct>[] = [
    {
      key: 'email',
      label: 'E-mail',
      align: 'left',
      sortValue: (r) => r.email ?? '',
      format: (_v, r) => <Link to={`/admin/payment/user?email=${encodeURIComponent(r.email ?? '')}`}>{r.email}</Link>,
    },
    { key: 'productName', label: 'Product', align: 'left', sortValue: (r) => r.productName ?? '' },
    { key: 'status', label: 'Status', align: 'right', numeric: true, sortValue: (r) => r.status ?? 0 },
    { key: 'price', label: 'Price', align: 'right', numeric: true, sortValue: (r) => r.price ?? 0, format: (_v, r) => formatMoney(r.price, r.currency) },
    { key: 'failCount', label: 'Fail count', align: 'right', numeric: true, sortValue: (r) => r.failCount, cellClass: (v) => (Number(v) > 0 ? 'portal-pnl-negative' : undefined) },
  ]

  return (
    <div>
      <div className="portal-page-title">
        <h1>Admin — payment overview</h1>
      </div>
      <div className="portal-card">
        <AdminNotice feedback={feedback} />
        <h3>Subscriptions {subscriptions ? `(${subscriptions.length})` : ''}</h3>
        {subscriptions === null ? <p>Loading…</p> : (
          <div className="portal-table-scroll">
            <Table columns={subColumns} rows={subscriptions} rowKey={(r, i) => `${r.email}-${r.plan}-${i}`} />
          </div>
        )}
        <h3>Products {products ? `(${products.length})` : ''}</h3>
        {products === null ? <p>Loading…</p> : (
          <div className="portal-table-scroll">
            <Table columns={productColumns} rows={products} rowKey={(r, i) => `${r.email}-${r.product}-${i}`} />
          </div>
        )}
      </div>
    </div>
  )
}
